// 认证相关类型定义
// 对应后端 src/models/auth_models.py 中的 Pydantic 模型

// 用户角色枚举（三层体系）
export enum UserRole {
  SUPER_ADMIN = 'super_admin',      // 超级管理员（系统级）
  COMPANY_ADMIN = 'company_admin',  // 公司管理员（租户级）
  USER = 'user'                     // 普通用户
}

// 用户请求基础模型（用于创建/注册，邮箱必填）
export interface UserRequestBase {
  username: string
  email: string
}

// 用户基础模型（用于响应，邮箱可选）
export interface UserBase {
  username: string
  email?: string
}

// 创建用户模型（管理员使用，邮箱必填）
export interface UserCreate extends UserRequestBase {
  password: string
  role: string // 'super_admin' | 'company_admin' | 'user'
}

// 用户注册模型（公开注册）
export interface UserRegister extends Omit<UserCreate, 'role'> {
  company_name: string
  company_description?: string
  role?: string // 注册时不发送，使用后端默认值"user"
}

// 用户登录模型
export interface UserLogin {
  username: string
  password: string
}

// 用户响应模型
export interface UserResponse extends UserBase {
  id: number
  company_id: number
  role: string
  is_active: boolean
  last_login?: string // ISO日期字符串
  created_at: string
  updated_at: string
  created_by?: number
}

// 公司基础模型
export interface CompanyBase {
  name: string
  description?: string
}

// 公司配置接口
// 注意：ui_language字段已弃用，语言配置统一使用general.language
export interface CompanySettings {
  general?: {
    language?: string
    timezone?: string
  }
  ai?: {
    model?: string
    temperature?: number
    max_tokens?: number
    enable_context_window?: boolean
    max_response_chars?: number
    enable_streaming?: boolean
    api_key?: string
    has_api_key?: boolean  // 指示是否有设置的API密钥
    embedding_model?: string  // "local" | "dashscope"
    embedding_api_key?: string  // 仅dashscope模型需要
    has_embedding_api_key?: boolean  // 指示是否有设置的嵌入API密钥
    embedding_endpoint_url?: string  // 仅local模型需要，Ollama endpoint URL
  }
  notifications?: {
    email_enabled?: boolean
    email_recipients?: string[]
    slack_enabled?: boolean
    slack_webhook?: string
    has_slack_webhook?: boolean  // 指示是否有设置的Slack Webhook
    enable_error_alerts?: boolean
  }
  integrations?: {
    enable_webhook?: boolean
    webhook_url?: string
    has_webhook_url?: boolean  // 指示是否有设置的Webhook URL
    enable_api?: boolean
    api_rate_limit?: number
    allowed_origins?: string[]
  }
}

// 公司响应模型（包含完整SaaS租户字段）
export interface CompanyResponse extends CompanyBase {
  id: number
  status: string  // active, suspended, expired, trial
  plan: string    // free, enterprise
  trial_ends_at?: string
  subscription_ends_at?: string
  max_users: number
  max_storage: number
  contact_email?: string
  contact_phone?: string
  billing_address?: Record<string, any>
  settings?: CompanySettings  // 租户配置字段
  created_at: string
  updated_at: string
}

// 令牌响应模型
export interface Token {
  access_token: string
  refresh_token?: string
  token_type: string
  expires_in: number
}

// 令牌数据模型（用于JWT payload）
export interface TokenData {
  user_id?: number
  company_id?: number
  role?: string
  username?: string
}

// 用户列表响应模型
export interface UserListResponse {
  items: UserResponse[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

// 修改密码请求模型
export interface PasswordChangeRequest {
  old_password: string
  new_password: string
}

// 更新用户信息请求模型
export interface UserUpdateRequest {
  username?: string
  email?: string
  is_active?: boolean
}

// 更新公司信息请求模型（包含完整SaaS租户字段）
export interface CompanyUpdateRequest {
  name?: string
  description?: string
  status?: string  // active, suspended, expired, trial
  plan?: string    // free, enterprise
  trial_ends_at?: string
  subscription_ends_at?: string
  max_users?: number
  max_storage?: number
  contact_email?: string
  contact_phone?: string
  billing_address?: Record<string, any>
  settings?: CompanySettings
}

// 租户统计信息
export interface TenantStats {
  company_id: number
  user_count: number
  active_user_count: number
  admin_count: number
  created_at: string
}

// 公司风格配置接口
export interface CompanyStyle {
  persona?: string;
  base_tone?: {
    formality_level?: string;
    politeness_level?: string;
    enthusiasm_level?: string;
  };
  emoji_policy?: string;
  response_preferences?: {
    use_emphasis?: boolean;
    line_breaks?: boolean;
  };
}

// 公司风格配置更新请求接口（嵌套格式）
export interface CompanyStyleUpdate {
  style_config: CompanyStyle;
}

// API响应包装器
export interface ApiResponse<T = any> {
  message?: string
  data?: T
  error?: string
}

// 认证状态
export interface AuthState {
  user: UserResponse | null
  company: CompanyResponse | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  isRestored: boolean  // 新增：状态恢复完成标志
}

// 登录表单数据
export interface LoginFormData {
  username: string
  password: string
}

// 注册表单数据
export interface RegisterFormData {
  username: string
  email: string
  password: string
  confirmPassword: string
  company_name: string
  company_description?: string
}

// 别名定义（用于向后兼容）
export type UserUpdate = UserUpdateRequest
export type PasswordChange = PasswordChangeRequest